import { ExerciseCreationModel } from "p6m-p6u";
import { Exercises, ExerciseTypes } from "../enums/exercises";
import { ExerciseDirections } from "../enums/directions";
import { Locales } from "../hooks/useExerciseDescriptionStructure";

export const MIN_WORDS_DEFAULT = 1;
export const MIN_WORDS_CONNECT_WORDS = 3;

/**
 * Returns the minimum number of words/items required for a given exercise type.
 *
 * Different exercise types have different minimum requirements:
 * - CONNECT_WORDS: Requires at least 3 words to create meaningful connections
 * - All other exercise types (including FILL_GAP): Require at least 1 word
 *
 * @param questionMode - The exercise question mode identifier (from ExerciseTypes map)
 * @returns The minimum word count required for the specified exercise type
 */
export const getMinWordsForExerciseType = (questionMode: string | undefined): number => {
    return questionMode === ExerciseTypes.get(Exercises.CONNECT_WORDS) ? MIN_WORDS_CONNECT_WORDS : MIN_WORDS_DEFAULT;
};

export const getExerciseTypeByQuestionMode = (exerciseType: string) => {
    const exerciseEnumKey = Array.from(ExerciseTypes.entries()).find(([_, value]) => value === exerciseType);

    return exerciseEnumKey && exerciseEnumKey[0];
};

/**
 * A "Textaufgabe" starts as FREE_TEXT and converts to CREATE_TEXT the moment the
 * first vocabulary word is added (vocab chips, direction selector and suggestions
 * appear). Mutates and returns the passed exercise.
 */
export const convertFreeTextToCreateText = (
    exercise: ExerciseCreationModel,
    getDefaultDescription: (exerciseType: Exercises, locale: Locales) => string
): ExerciseCreationModel => {
    exercise.questionMode = ExerciseTypes.get(Exercises.CREATE_TEXT);
    exercise.direction = exercise.direction ?? ExerciseDirections.QUESTION_TO_ANSWER;
    delete exercise.includeEmptyFreeTextField;

    // If no task yet → auto-fill the default (ExerciseTask flashes it). If a task
    // already exists it is kept and the default shows up as a suggestion bubble.
    if (!exercise.extraComments) {
        exercise.extraComments = getDefaultDescription(Exercises.CREATE_TEXT, Locales.DE);
    }

    return exercise;
};

/**
 * Reverse of convertFreeTextToCreateText: removing the last vocabulary word from a
 * CREATE_TEXT exercise reverts it back to a plain FREE_TEXT task. A task text
 * (extraComments) typed by the user is kept; the auto-injected CREATE_TEXT default
 * is cleared again. Mutates and returns the passed exercise.
 */
export const convertCreateTextToFreeText = (
    exercise: ExerciseCreationModel,
    getDefaultDescription: (exerciseType: Exercises, locale: Locales) => string
): ExerciseCreationModel => {
    exercise.questionMode = ExerciseTypes.get(Exercises.FREE_TEXT);
    exercise.includeEmptyFreeTextField = true;
    delete exercise.direction;

    if (exercise.extraComments === getDefaultDescription(Exercises.CREATE_TEXT, Locales.DE)) {
        exercise.extraComments = "";
    }

    return exercise;
};
